import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Drillable, DrillValue } from '../components/Drill'
import { useSearchParams } from 'react-router-dom'
import {
  getEmails,
  previewEmail,
  resetEmail,
  saveEmail,
  sendTestEmail,
  type EmailContent,
  type EmailField,
  type EmailGroup,
  type EmailPoint,
  type EmailsReport,
  type ManagedEmail
} from '../lib/adminClient'
import { useAuth } from '../lib/auth'
import { LoadError } from '../components/LoadError'
import { dateTime, num } from '../lib/format'
import SentLog from './emails/SentLog'
import SendEmail from './emails/SendEmail'

// The emails Plexii sends on its own (server: emailSettings.ts). Owner,
// 2026-10-30: "where can i manage the content and cadence of emails being
// sent". Each one can be reworded, retimed, previewed, test-sent to yourself
// and switched off. The links, the code, the unsubscribe line and the sender
// footer are added by the server whatever the wording, so they are shown in the
// preview but cannot be edited here. A change applies from the next hourly run.

const GROUPS: Array<{ group: EmailGroup; title: string; note: string }> = [
  { group: 'trial', title: 'Trial', note: 'Counted from the day they confirm their email. The trial lasts 14 days.' },
  { group: 'signup', title: 'Sign-up reminders', note: 'For people who signed up but never confirmed their email. Each one carries a fresh code.' },
  { group: 'checkout', title: 'Unfinished checkout', note: 'For people who started paying for a plan and did not finish.' },
  {
    group: 'upgrade',
    title: 'Upgrade follow-ups',
    note: 'A series of four for people who press Upgrade in the app and do not pay. Off unless you switch them on.'
  }
]

const unitWord = (n: number, unit: 'hours' | 'days'): string => (n === 1 ? unit.slice(0, -1) : unit)
const whenText = (e: ManagedEmail, value = e.timing.value): string => `${value} ${unitWord(value, e.timing.unit)} ${e.timing.phrase}`
const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b)
const toParagraphText = (list: string[]): string => list.join('\n\n')
const fromParagraphText = (text: string): string[] => text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)

type LoadState = { s: 'loading' } | { s: 'ready'; r: EmailsReport } | { s: 'error'; error: string }

// Three tabs, kept in the address so a link opens the right one:
//   ?tab=log                  the Sent log
//   ?tab=send&to=<accountId>  write an email, with that person chosen (the Users page's Email button)
//   ?tab=send&send=<id>       one earlier send, with its people
type Tab = 'automatic' | 'log' | 'send'
const TABS: Array<{ tab: Tab; label: string; intro: string }> = [
  {
    tab: 'automatic',
    label: 'Automatic',
    intro: 'The emails Plexii sends on its own. Change what they say or when they go, preview them, send yourself a test, or switch one off. Changes apply from the next hourly run.'
  },
  { tab: 'log', label: 'Sent log', intro: 'Every email the server sent, who it went to, and whether it went. Filter by any column, or download what you filtered as CSV.' },
  { tab: 'send', label: 'Send an email', intro: 'Write an email and send it to one person or a group you choose.' }
]

export default function Emails(): JSX.Element {
  const session = useAuth()
  const canWrite = session?.role !== 'support'
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab')
  const tab: Tab = raw === 'log' || raw === 'send' ? raw : 'automatic'
  const go = (next: Record<string, string>): void => setParams(next)
  const current = TABS.find((x) => x.tab === tab)!

  return (
    <div>
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Growth</div>
        <h1 className="text-2xl font-semibold mt-1">Emails</h1>
        <div className="text-sm text-slate-400 mt-1 max-w-3xl">{current.intro}</div>
        {!canWrite && (
          <div className="text-xs text-amber-200 mt-2" data-testid="emails-readonly">
            You can look, but changing or sending emails needs an admin account.
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mb-5 border-b border-white/10" role="tablist">
        {TABS.map((x) => (
          <button
            key={x.tab}
            role="tab"
            aria-selected={tab === x.tab}
            onClick={() => go(x.tab === 'automatic' ? {} : { tab: x.tab })}
            data-testid={`emails-tab-${x.tab}`}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === x.tab ? 'border-accent text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          >
            {x.label}
          </button>
        ))}
      </div>
      {tab === 'automatic' && <AutomaticEmails canWrite={canWrite} adminEmail={session?.email ?? ''} />}
      {tab === 'log' && <SentLog onOpenCampaign={(id) => go({ tab: 'send', send: id })} initialSearch={params.get('q')} />}
      {tab === 'send' && (
        <SendEmail
          canWrite={canWrite}
          preselect={params.get('to')}
          openCampaignId={params.get('send')}
          onOpenCampaign={(id) => go(id ? { tab: 'send', send: id } : { tab: 'send' })}
        />
      )}
    </div>
  )
}

function AutomaticEmails({ canWrite, adminEmail }: { canWrite: boolean; adminEmail: string }): JSX.Element {
  const [state, setState] = useState<LoadState>({ s: 'loading' })
  const [open, setOpen] = useState<string | null>(null)
  const [toggleError, setToggleError] = useState<{ key: string; error: string } | null>(null)

  const load = useCallback(async () => {
    setState({ s: 'loading' })
    const res = await getEmails()
    setState(res.ok ? { s: 'ready', r: res.data } : { s: 'error', error: res.error })
  }, [])
  useEffect(() => { void load() }, [load])

  const replace = (email: ManagedEmail): void =>
    setState((st) => (st.s === 'ready' ? { s: 'ready', r: { ...st.r, emails: st.r.emails.map((e) => (e.key === email.key ? email : e)) } } : st))

  async function toggle(e: ManagedEmail): Promise<void> {
    setToggleError(null)
    const res = await saveEmail(e.key, { enabled: !e.enabled })
    if (res.ok) replace(res.data.email)
    else setToggleError({ key: e.key, error: res.error })
  }

  return (
    <div>
      {state.s === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading emails…</div>}
      {state.s === 'error' && <LoadError what="emails" endpoint="GET /admin/emails" error={state.error} testId="emails-error" />}

      {state.s === 'ready' && (() => {
        const { r } = state
        const sent30 = r.emails.reduce((n, e) => n + e.stats.last30, 0)
        // The server's own start of "the last 30 days", so the records opened are the ones counted.
        const since = r.since ?? Date.now() - 30 * 86_400_000
        const checkoutOn = r.emails.find((e) => e.key === 'checkout_1')?.enabled
        const upgradeOn = r.emails.some((e) => e.group === 'upgrade' && e.enabled)
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <Drillable
                to={{ set: 'automatedSends', filters: { afterMs: String(since) } }}
                label="Automated emails sent in the last 30 days"
                expect={{ key: 'count', value: sent30 }}
                className="glass-card rounded-xl p-4"
                testId="emails-sent"
              >
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Sent in the last 30 days</div>
                <div className="text-2xl font-semibold mt-1 tabular-nums">{num(sent30)}</div>
                <div className="text-xs text-slate-400 mt-1">Across the emails below</div>
              </Drillable>
              <Drillable to={{ set: 'unsubscribes', filters: {} }} label="Unsubscribes" expect={{ key: 'count', value: r.unsubscribes.total }} className="glass-card rounded-xl p-4" testId="emails-unsubscribes">
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Unsubscribed</div>
                <div className="text-2xl font-semibold mt-1 tabular-nums">{num(r.unsubscribes.total)}</div>
                <div className="text-xs text-slate-400 mt-1">
                  <DrillValue to={{ set: 'unsubscribes', filters: { afterMs: String(since) } }} label="Unsubscribes in the last 30 days" expect={{ key: 'count', value: r.unsubscribes.last30 }}>
                    {num(r.unsubscribes.last30)}
                  </DrillValue>{' '}
                  in the last 30 days
                </div>
              </Drillable>
              <div className="glass-card rounded-xl p-4" data-testid="emails-sender">
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Footer of every marketing email</div>
                <div className="text-sm text-slate-100 mt-2">{r.sender.name}</div>
                <div className="text-xs text-slate-400 mt-0.5 break-words">{r.sender.address}</div>
              </div>
            </div>

            {checkoutOn && upgradeOn && (
              <div className="glass-card rounded-xl p-3 mb-4 text-sm text-amber-200 border border-amber-400/20" data-testid="emails-overlap">
                The checkout reminder and the upgrade follow-ups are both on. Both follow up an upgrade that was not paid for, so
                one person can get both.
              </div>
            )}

            {GROUPS.map((g) => {
              const list = r.emails.filter((e) => e.group === g.group)
              if (list.length === 0) return null
              return (
                <section key={g.group} className="mb-6" data-testid={`emails-group-${g.group}`}>
                  <div className="mb-2">
                    <h2 className="text-base font-semibold text-slate-100">{g.title}</h2>
                    <div className="text-xs text-slate-400">{g.note}</div>
                  </div>
                  <div className="glass-card rounded-xl divide-y divide-white/5">
                    {list.map((e) => (
                      <div key={e.key} data-testid={`email-row-${e.key}`}>
                        <div
                          className="p-4 flex flex-wrap items-center gap-4 cursor-pointer hover:bg-white/[0.02]"
                          onClick={() => setOpen(open === e.key ? null : e.key)}
                          aria-expanded={open === e.key}
                          data-testid={`email-rowhead-${e.key}`}
                        >
                          <button
                            role="switch"
                            aria-checked={e.enabled}
                            aria-label={`${e.name}: ${e.enabled ? 'on' : 'off'}`}
                            disabled={!canWrite}
                            onClick={(ev) => { ev.stopPropagation(); void toggle(e) }}
                            data-testid={`email-toggle-${e.key}`}
                            title={canWrite ? (e.enabled ? 'Switch off' : 'Switch on') : 'Needs an admin account'}
                            className={`relative w-10 h-6 rounded-full transition-colors shrink-0 disabled:opacity-50 ${e.enabled ? 'bg-emerald-500/70' : 'bg-white/10'}`}
                          >
                            <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${e.enabled ? 'left-5' : 'left-1'}`} />
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-slate-100 font-medium">{e.name}</span>
                              <span className={`text-[11px] px-1.5 py-0.5 rounded ${e.enabled ? 'bg-emerald-500/15 text-emerald-200' : 'bg-white/5 text-slate-400'}`} data-testid={`email-status-${e.key}`}>
                                {e.enabled ? 'On' : 'Off'}
                              </span>
                              {(e.changed.content || e.changed.timing) && (
                                <span className="text-[11px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-200" data-testid={`email-changed-${e.key}`}>Edited</span>
                              )}
                              {e.marketing && <span className="text-[11px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400" title="Carries an unsubscribe link; never sent to anyone who unsubscribed">Marketing</span>}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">{whenText(e)} · {e.who}</div>
                            <div className="text-sm text-slate-300 mt-1 truncate" title={String(e.content.subject)}>“{String(e.content.subject)}”</div>
                            {e.ignored && <div className="text-xs text-amber-200 mt-1">{e.ignored}</div>}
                            {toggleError?.key === e.key && <div className="text-xs text-red-300 mt-1">{toggleError.error}</div>}
                          </div>
                          <div className="text-right text-xs text-slate-400 tabular-nums" data-testid={`email-stats-${e.key}`}>
                            <div>
                              <span className="text-slate-200">
                                {e.records ? (
                                  <DrillValue to={{ set: 'automatedSends', filters: { ...e.records, afterMs: String(r.since ?? Date.now() - 30 * 86_400_000) } }} label={`${e.name}, sent in the last 30 days`} expect={{ key: 'count', value: e.stats.last30 }} testId={`email-sends-${e.key}`}>
                                    {num(e.stats.last30)}
                                  </DrillValue>
                                ) : (
                                  num(e.stats.last30)
                                )}
                              </span>{' '}
                              sent in 30 days
                            </div>
                            <div>{e.stats.lastSentAt ? `last ${dateTime(e.stats.lastSentAt)}` : 'never sent'}</div>
                          </div>
                          <button className="btn-ghost" onClick={(ev) => { ev.stopPropagation(); setOpen(open === e.key ? null : e.key) }} data-testid={`email-edit-${e.key}`}>
                            {open === e.key ? 'Close' : canWrite ? 'Edit' : 'View'}
                          </button>
                        </div>
                        {open === e.key && <Editor email={e} canWrite={canWrite} adminEmail={adminEmail} onSaved={replace} />}
                      </div>
                    ))}
                  </div>
                </section>
              )
            })}

            <section className="mb-6" data-testid="emails-always">
              <h2 className="text-base font-semibold text-slate-100 mb-1">Always sent</h2>
              <div className="text-xs text-slate-400 mb-2">Emails people trigger themselves. They always go, and are not changed here.</div>
              <div className="glass-card rounded-xl divide-y divide-white/5">
                {r.alwaysSent.map((a) => (
                  <div key={a.name} className="px-4 py-2.5 text-sm flex flex-wrap gap-x-3">
                    <span className="text-slate-200">{a.name}</span>
                    <span className="text-slate-400">{a.when}</span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )
      })()}
    </div>
  )
}

// ── The editor ──────────────────────────────────────────────────────────────

const inputClass =
  'w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-accent/50 disabled:opacity-60'

function Editor({ email, canWrite, adminEmail, onSaved }: { email: ManagedEmail; canWrite: boolean; adminEmail: string; onSaved: (e: ManagedEmail) => void }): JSX.Element {
  const [content, setContent] = useState<EmailContent>(email.content)
  const [timing, setTiming] = useState<number>(email.timing.value)
  const [timingText, setTimingText] = useState(String(email.timing.value))
  const [busy, setBusy] = useState<'save' | 'test' | 'reset' | null>(null)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const [preview, setPreview] = useState<{ subject: string; html: string } | { error: string } | null>(null)
  const seq = useRef(0)

  const dirty = !same(content, email.content) || timing !== email.timing.value
  const fixedTiming = email.timing.min === email.timing.max
  const draft = useMemo(() => ({ content, timing }), [content, timing])

  // The preview follows the draft, a moment after typing stops.
  useEffect(() => {
    const n = ++seq.current
    const t = setTimeout(async () => {
      const res = await previewEmail(email.key, draft)
      if (n !== seq.current) return
      setPreview(res.ok ? { subject: res.data.subject, html: res.data.html } : { error: res.error })
    }, 350)
    return () => clearTimeout(t)
  }, [email.key, draft])

  const set = (key: string, value: EmailContent[string]): void => setContent((c) => ({ ...c, [key]: value }))

  async function save(): Promise<void> {
    setBusy('save')
    setMessage(null)
    const change: { content?: EmailContent; timing?: number } = {}
    if (!same(content, email.content)) change.content = content
    if (timing !== email.timing.value) change.timing = timing
    const res = await saveEmail(email.key, change)
    setBusy(null)
    if (!res.ok) return setMessage({ tone: 'error', text: res.error })
    onSaved(res.data.email)
    setContent(res.data.email.content)
    setTiming(res.data.email.timing.value)
    setTimingText(String(res.data.email.timing.value))
    setMessage({ tone: 'ok', text: 'Saved. It applies from the next hourly run.' })
  }

  async function test(): Promise<void> {
    setBusy('test')
    setMessage(null)
    const res = await sendTestEmail(email.key, draft)
    setBusy(null)
    if (!res.ok) return setMessage({ tone: 'error', text: res.error })
    setMessage(
      res.data.delivered
        ? { tone: 'ok', text: `Test sent to ${res.data.to}. It shows what is on screen now, saved or not.` }
        : { tone: 'error', text: 'Not sent: the server has no email provider set up.' }
    )
  }

  async function reset(): Promise<void> {
    setBusy('reset')
    setMessage(null)
    setConfirmReset(false)
    const res = await resetEmail(email.key)
    setBusy(null)
    if (!res.ok) return setMessage({ tone: 'error', text: res.error })
    onSaved(res.data.email)
    setContent(res.data.email.content)
    setTiming(res.data.email.timing.value)
    setTimingText(String(res.data.email.timing.value))
    setMessage({ tone: 'ok', text: 'Put back as written: wording, timing and on/off.' })
  }

  const isDefault = !email.changed.content && !email.changed.timing && !email.changed.enabled

  return (
    <div className="px-4 pb-5 grid gap-5 lg:grid-cols-2" data-testid={`email-editor-${email.key}`}>
      <div className="min-w-0 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">When it goes</div>
          {fixedTiming ? (
            <div className="text-sm text-slate-200">{whenText(email)}. This one cannot move.</div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-200">
              <input
                type="number"
                min={email.timing.min}
                max={email.timing.max}
                step={1}
                value={timingText}
                disabled={!canWrite}
                onChange={(ev) => {
                  setTimingText(ev.target.value)
                  const n = Number(ev.target.value)
                  if (ev.target.value !== '' && Number.isFinite(n)) setTiming(n)
                }}
                data-testid="email-timing"
                className="w-20 rounded-lg bg-white/[0.03] border border-white/10 px-2 py-1.5 text-sm text-slate-100 tabular-nums"
              />
              <span>{unitWord(timing, email.timing.unit)} {email.timing.phrase}</span>
              <span className="text-xs text-slate-500">
                ({email.timing.min}–{email.timing.max}; as written {email.timing.default})
              </span>
            </div>
          )}
        </div>

        {email.fields.map((f) => (
          <FieldEditor key={f.key} field={f} value={content[f.key]} disabled={!canWrite} onChange={(v) => set(f.key, v)} />
        ))}

        {email.placeholders.length > 0 && (
          <div className="text-xs text-slate-400" data-testid="email-placeholders">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">Placeholders you can type</div>
            {email.placeholders.map((p) => (
              <div key={p.name}>
                <code className="text-violet-200">{`{${p.name}}`}</code> becomes {p.meaning}.
              </div>
            ))}
          </div>
        )}
        <div className="text-xs text-slate-400">{email.always}</div>

        {canWrite && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button className="btn-primary" disabled={!dirty || busy !== null} onClick={() => void save()} data-testid="email-save">
              {busy === 'save' ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn-ghost" disabled={busy !== null} onClick={() => void test()} data-testid="email-test" title={`Sends what is on screen to ${adminEmail}`}>
              {busy === 'test' ? 'Sending…' : 'Send me a test'}
            </button>
            {!confirmReset ? (
              <button className="btn-ghost" disabled={busy !== null || isDefault} onClick={() => setConfirmReset(true)} data-testid="email-reset">
                Put back as written
              </button>
            ) : (
              <span className="flex items-center gap-2 text-sm text-slate-300" data-testid="email-reset-confirm">
                Undo every change to this email?
                <button className="btn-primary" onClick={() => void reset()} data-testid="email-reset-yes">Yes, put it back</button>
                <button className="btn-ghost" onClick={() => setConfirmReset(false)}>No</button>
              </span>
            )}
            {dirty && <span className="text-xs text-amber-200" data-testid="email-unsaved">Unsaved changes</span>}
          </div>
        )}
        {message && (
          <div className={`text-sm ${message.tone === 'ok' ? 'text-emerald-200' : 'text-red-300'}`} data-testid="email-message">{message.text}</div>
        )}
        {email.updatedAt && (
          <div className="text-xs text-slate-500">Last changed {dateTime(email.updatedAt)}{email.updatedBy ? ` by ${email.updatedBy}` : ''}.</div>
        )}
      </div>

      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">Preview</div>
        {preview === null && <div className="text-sm text-slate-400">Building the preview…</div>}
        {preview && 'error' in preview && <div className="text-sm text-red-300" data-testid="email-preview-error">Can't preview this yet: {preview.error}</div>}
        {preview && 'html' in preview && (
          <div className="rounded-xl overflow-hidden border border-white/10">
            <div className="px-3 py-2 text-sm bg-white/[0.04] border-b border-white/10">
              <span className="text-slate-400">Subject: </span>
              <span className="text-slate-100" data-testid="email-preview-subject">{preview.subject}</span>
            </div>
            <iframe title={`${email.name} preview`} sandbox="" srcDoc={preview.html} className="w-full h-[560px] bg-white" data-testid="email-preview-frame" />
          </div>
        )}
        <div className="text-xs text-slate-500 mt-1">Placeholders are filled with your name and sample values (code 123456, the Pro plan).</div>
      </div>
    </div>
  )
}

function FieldEditor({ field, value, disabled, onChange }: { field: EmailField; value: EmailContent[string]; disabled: boolean; onChange: (v: EmailContent[string]) => void }): JSX.Element {
  const label = <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">{field.label}</div>
  if (field.kind === 'line') {
    const v = String(value ?? '')
    return (
      <div>
        {label}
        <input value={v} disabled={disabled} maxLength={field.max + 50} onChange={(e) => onChange(e.target.value)} data-testid={`email-field-${field.key}`} className={inputClass} />
        {v.length > field.max && <div className="text-xs text-red-300 mt-1">{v.length} characters; the limit is {field.max}.</div>}
      </div>
    )
  }
  if (field.kind === 'text') {
    return (
      <div>
        {label}
        <textarea value={String(value ?? '')} disabled={disabled} rows={3} onChange={(e) => onChange(e.target.value)} data-testid={`email-field-${field.key}`} className={inputClass} />
      </div>
    )
  }
  if (field.kind === 'paragraphs') {
    return <ParagraphsEditor field={field} value={(value as string[]) ?? []} disabled={disabled} onChange={onChange} label={label} />
  }
  const points = ((value as EmailPoint[]) ?? []).map((p) => ({ ...p }))
  const update = (i: number, patch: Partial<EmailPoint>): void => onChange(points.map((p, j) => (j === i ? { ...p, ...patch } : p)))
  return (
    <div>
      {label}
      <div className="space-y-3">
        {points.map((p, i) => (
          <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2" data-testid="email-point">
            <div className="flex gap-2">
              <input value={p.title} disabled={disabled} placeholder="Heading" onChange={(e) => update(i, { title: e.target.value })} className={inputClass} />
              {!disabled && points.length > 1 && (
                <button className="btn-ghost shrink-0" onClick={() => onChange(points.filter((_, j) => j !== i))} aria-label={`Remove point ${i + 1}`}>Remove</button>
              )}
            </div>
            <textarea value={p.body} disabled={disabled} rows={2} placeholder="What it does" onChange={(e) => update(i, { body: e.target.value })} className={inputClass} />
          </div>
        ))}
      </div>
      {!disabled && points.length < (field.maxItems ?? 5) && (
        <button className="btn-ghost mt-2" onClick={() => onChange([...points, { title: '', body: '' }])} data-testid="email-add-point">Add a point</button>
      )}
    </div>
  )
}

// Typed as one box with a blank line between paragraphs. The text is kept as
// typed while editing, so a blank line being typed does not vanish.
function ParagraphsEditor({ field, value, disabled, onChange, label }: { field: EmailField; value: string[]; disabled: boolean; onChange: (v: string[]) => void; label: JSX.Element }): JSX.Element {
  const [text, setText] = useState(toParagraphText(value))
  const last = useRef(value)
  useEffect(() => {
    if (!same(value, last.current) && !same(value, fromParagraphText(text))) setText(toParagraphText(value))
    last.current = value
  }, [value, text])
  return (
    <div>
      {label}
      <textarea
        value={text}
        disabled={disabled}
        rows={Math.min(14, Math.max(5, text.split('\n').length + 1))}
        onChange={(e) => {
          setText(e.target.value)
          const next = fromParagraphText(e.target.value)
          last.current = next
          onChange(next)
        }}
        data-testid={`email-field-${field.key}`}
        className={inputClass}
      />
      <div className="text-xs text-slate-500 mt-1">Leave a blank line between paragraphs. Up to {field.maxItems} paragraphs.</div>
    </div>
  )
}
